import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import { AdminAutomotiveNav } from "../automotive/AdminAutomotiveNav";
import { CITY_STATUS_LABELS, READINESS_LABELS } from "../../../constants/automapCityExpansionConstants";
import { fetchExpansionDashboard, trackCityExpansionEvent } from "../../../services/automapCityExpansionApi";
import { AdminExpansionNav } from "./AdminExpansionShared";

function MetricCard({ label, value, href }) {
  const inner = (
    <>
      <span className="admin-dashboard__metric-label">{label}</span>
      <strong className="admin-dashboard__metric-value">{value ?? "—"}</strong>
    </>
  );
  return href ? (
    <Link to={href} className="admin-dashboard__metric-card admin-dashboard__metric-card--link">{inner}</Link>
  ) : (
    <div className="admin-dashboard__metric-card">{inner}</div>
  );
}

export default function AdminExpansionDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await fetchExpansionDashboard();
      setData(payload.dashboard);
      trackCityExpansionEvent("city_expansion_dashboard_viewed");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const t = data?.totals || {};

  return (
    <AdminPageLayout>
      <AdminPageHeader eyebrow="AutoMapa · Expansión" title="City Expansion Toolkit" description="Lanzamiento repetible por ciudad: supply, demand, SEO, campañas y readiness." />
      <AdminAutomotiveNav active="expansion" />
      <AdminExpansionNav active="dashboard" />
      <main className="admin-console__main wrap">
        {loading ? <p className="dealer-state-box">Cargando…</p> : null}
        {error ? <p className="dealer-state-box">{error}</p> : null}
        {!loading && data ? (
          <>
            <div className="admin-dashboard__metrics-grid">
              <MetricCard label="Planificadas" value={t.planned} href="/admin/expansion/cities" />
              <MetricCard label="Adquisición dealers" value={t.dealerAcquisition} />
              <MetricCard label="Inventario en build" value={t.inventoryBuilding} />
              <MetricCard label="Soft launch ready" value={t.softLaunchReady} />
              <MetricCard label="Activas" value={t.active} />
              <MetricCard label="Dealers expansión" value={t.expansionDealers} />
              <MetricCard label="Listings expansión" value={t.expansionListings} />
              <MetricCard label="Necesitan atención" value={t.needingAttention} />
            </div>
            {data.nextTargetCity ? (
              <p className="dealer-state-box">
                Próxima ciudad objetivo: <Link to={`/admin/expansion/cities/${data.nextTargetCity.citySlug}`}>{data.nextTargetCity.city}</Link>
                {" · "}Readiness {data.nextTargetCity.launchReadinessScore}
              </p>
            ) : null}
            <section className="dealer-state-box">
              <h3>Acciones recomendadas</h3>
              {!data.recommendedActions?.length ? <p className="muted">Sin acciones pendientes.</p> : (
                <ul>{data.recommendedActions.map((a) => <li key={a.id}><Link to={a.href}>{a.message}</Link></li>)}</ul>
              )}
            </section>
            <div className="import-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Ciudad</th>
                    <th>Estado</th>
                    <th>Stage</th>
                    <th>Dealers</th>
                    <th>Listings</th>
                    <th>Freshness</th>
                    <th>SEO</th>
                    <th>Readiness</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(data.cities || []).map((row) => (
                    <tr key={row.citySlug}>
                      <td>{row.city}</td>
                      <td>{CITY_STATUS_LABELS[row.status] || row.status}</td>
                      <td>{row.launchStage}</td>
                      <td>{row.activeDealers}/{row.targetDealerCount}</td>
                      <td>{row.activeListings}/{row.targetActiveListingCount}</td>
                      <td>{row.freshnessPercent}%</td>
                      <td>{row.seoReadiness}</td>
                      <td>{row.launchReadinessScore} · {READINESS_LABELS[row.readinessLabel] || row.readinessLabel}</td>
                      <td><Link to={`/admin/expansion/cities/${row.citySlug}`}>Abrir</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Link className="admin-btn admin-btn--primary" to="/admin/expansion/cities/new">Nueva ciudad</Link>
          </>
        ) : null}
      </main>
    </AdminPageLayout>
  );
}
